import type { ScanBand, ScanFacets } from "../../../../lib/siteScan";
import type { ScanFilterState } from "./useScansTable";

/**
 * Which scan filter belongs to which group, and what is currently set.
 *
 * React-free on purpose: the toolbar above the table is now four dropdowns and
 * a chip row, and both need the same answer to "what is on right now". Working
 * that out inside the component would mean the chip row and the menus could
 * drift apart - which is exactly the bug where a chip offers to remove a filter
 * the menu no longer shows.
 */

export const BANDS: ScanBand[] = ["hot", "warm", "cool", "healthy"];

/** What the site itself is like: how it is built and whether it answers. */
export const SITE_FLAGS = [
  "outdated",
  "abandoned",
  "insecure",
  "malwareConfirmed",
  "malwareSuspected",
  "noHttps",
  "noMobile",
  "dead",
] as const;

/** Whether we know who to call. */
export const CONTACT_FLAGS = ["hasEmail", "hasCompany"] as const;

export type ScanFlag = (typeof SITE_FLAGS)[number] | (typeof CONTACT_FLAGS)[number];

/**
 * The facet count for a flag, where the API counts it.
 *
 * Four of the ten have no facet: "abandoned plugins", "insecure WordPress",
 * "no HTTPS" and "no mobile view" are computed per row rather than aggregated,
 * so their options show a label alone. A wrong count would be worse than none.
 */
export function flagCount(flag: ScanFlag, facets: ScanFacets | null): number | undefined {
  if (!facets) return undefined;
  switch (flag) {
    case "outdated":
      return facets.outdated;
    case "dead":
      return facets.dead;
    case "malwareConfirmed":
      return facets.malwareConfirmed;
    case "malwareSuspected":
      return facets.malwareSuspected;
    case "hasEmail":
      return facets.withEmail;
    case "hasCompany":
      return facets.withCompany;
    default:
      return undefined;
  }
}

/** The flags of a group that are currently on, as menu values. */
export function selectedFlags(filters: ScanFilterState, group: readonly ScanFlag[]): string[] {
  return group.filter((flag) => filters[flag]);
}

/** Turn a group's flags back off in one patch. */
export function clearFlags(group: readonly ScanFlag[]): Partial<ScanFilterState> {
  return Object.fromEntries(group.map((flag) => [flag, false]));
}

/** One thing currently narrowing the list, and how to stop it narrowing. */
export interface ScanFilterChip {
  key: string;
  label: string;
  patch: Partial<ScanFilterState>;
}

/**
 * Everything that is on, in the order the groups sit in the toolbar.
 *
 * Search is deliberately absent: it has its own visible field, and a chip for
 * it would offer a second, quieter way to clear something already on screen.
 */
export function scanFilterChips(
  filters: ScanFilterState,
  label: (key: string) => string,
): ScanFilterChip[] {
  const chips: ScanFilterChip[] = [];

  for (const band of filters.band ?? []) {
    chips.push({
      key: `band:${band}`,
      label: label(`scanner.score.${band}`),
      patch: { band: (filters.band ?? []).filter((b) => b !== band) },
    });
  }

  for (const kind of filters.kind ?? []) {
    chips.push({
      key: `kind:${kind}`,
      label: label(`scanner.kind.${kind}`),
      patch: { kind: (filters.kind ?? []).filter((k) => k !== kind) },
    });
  }

  for (const id of filters.cms) {
    chips.push({
      key: `cms:${id}`,
      label: id === "none" ? label("scanner.table.noPlatform") : id,
      patch: { cms: filters.cms.filter((c) => c !== id) },
    });
  }

  for (const flag of [...SITE_FLAGS, ...CONTACT_FLAGS]) {
    if (!filters[flag]) continue;
    chips.push({
      key: `flag:${flag}`,
      label: label(`scanner.table.filter.${flag}`),
      patch: { [flag]: false },
    });
  }

  return chips;
}
